import React from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Row,
  Col,
  Divider,
  message,
} from 'antd';
import Select from 'components/Select';
import NumericInput from 'components/NumericInput';
import { INE_PATTERN } from 'utils/patterns';
import { capitalize } from 'utils/normalizers';
import LadaNumero from 'components/LadaNumero';
import { InputSearchPro } from 'components/InputSearch';
import { onError } from 'utils/handlers';
import { getCiudadanoCaja } from 'api/cuentaunicasir/index';
import { generateCURPRules, generateRFCRules } from 'utils/rules';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { useSelector } from 'react-redux';
import { handleOnBlur } from 'views/Catastro/utils/onBlurKey';

const ENV_NAME = process.env.REACT_APP_ENV_NAME || 'migob';
const IPROVINAY = ENV_NAME === 'iprovinay';
const TLAJOMULCO = ENV_NAME === 'tlajomulco';

const BusquedaFormsPadrones = ({
  catalogs,
  tipoDePadron,
  form,
  disabled,
  setLoading,
  setCiudadanoFound,
  ciudadanoFound,
  visibleRFC,
  visibleCURP,
}) => {
  const padrones = useSelector(({ catalogs: cats }) => cats.padrones);
  const tipoPropietario = Form.useWatch('tipo_propietario', form);
  const {
    generos,
    estadosCiviles,
    tiposDeEstablecimiento,
    unidadesRecaudadoras,
    tiposDeZonaExclusiva,
    tipoDeContrato,
    tipoDeContratoPanteones,
    tiposDeEspacios,
    tiposDePanteones,
    zonas,
  } = catalogs;
  const rules = {
    required: [{ required: true }],
    sitio_web: [
      {
        type: 'url',
        message: 'Ingrese una URL válida',
      },
    ],
    email: [{ type: 'email' }],
    RFC: generateRFCRules({ tipoDePadron, tipoDePersona: 2 }),
    CURP: generateCURPRules({ tipoDePadron }),
    INE: [
      {
        validator: async (_, value) => {
          if (value) {
            if (!INE_PATTERN.test(value)) {
              throw new Error('Ingrese un INE válido');
            }
          }
        },
      },
    ],
    nombre: [
      {
        validator: async (_, value) => {
          const { apellido_paterno, apellido_materno } = form.getFieldsValue();
          if (value && !apellido_paterno && !apellido_materno) {
            throw new Error('Debe ingresar mínimo dos campos referentes a nombre');
          }
        },
      },
    ],
    apellido_paterno: [
      {
        validator: async (_, value) => {
          const { nombre, apellido_materno } = form.getFieldsValue();
          if (value && !nombre && !apellido_materno) {
            throw new Error('Debe ingresar mínimo dos campos referentes a nombre');
          }
        },
      },
    ],
    apellido_materno: [
      {
        validator: async (_, value) => {
          const { apellido_paterno, nombre } = form.getFieldsValue();
          if (value && !apellido_paterno && !nombre) {
            throw new Error('Debe ingresar mínimo dos campos referentes a nombre');
          }
        },
      },
    ],
    numero_de_celular: [
      { required: false },
      {
        pattern: /^(?:[0-9]{10}|)$/,
        message: 'Ingrese un número telefónico válido',
      },
    ],
  };

  const resetAndValidate = () => {
    setCiudadanoFound([]);
    form.resetFields(['q']);
  };

  const searchCiudadano = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _ciudadano = await getCiudadanoCaja({ q });
        if (_ciudadano.results.length) {
          const [selected] = _ciudadano.results;
          form.setFieldsValue({
            ciudadano: selected.id,
          });
          setCiudadanoFound([selected]);
        } else if (!_ciudadano.results.length) {
          message.info('No se encontró al ciudadano');
          // setVisibleBusquedaAvanzadaCiudadano(true);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  // Padrones derivados de Empresa
  const DefaultForm = () => (
    <Row gutter={10}>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          label="clave"
          name="clave"
          normalize={capitalize}
        >
          <Input allowClear />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="descripcion"
          label="Descripción"
          normalize={capitalize}
        >
          <Input allowClear />
        </Form.Item>
      </Col>
      {visibleRFC && (
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            label="RFC"
            name="rfc"
            rules={rules.RFC}
            normalize={(value) => value.toUpperCase()}
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      )}
      {visibleCURP && (
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            label="CURP"
            name="curp"
            normalize={capitalize}
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      )}

      <Col xs={24} sm={24} md={8}>
        <Form.Item
          label="unidad recaudadora"
          name="unidad_recaudadora"
        >
          <Select dataSource={unidadesRecaudadoras} />
        </Form.Item>
      </Col>
    </Row>
  );

  // Form de Ciudadano y Contribuyente
  const CiudadanoForm = () => (
    <Row gutter={10}>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name={tipoDePadron === 1 ? 'clave_ciudadana' : 'clave'}
          label="Clave"
          hasFeedback
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="nombre"
          label="Nombre"
          hasFeedback
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="apellido_paterno"
          label="Apellido paterno"
          hasFeedback
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="apellido_materno"
          label="Apellido materno"
          hasFeedback
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      {tipoDePadron === 15 && (
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="razon_social"
            label="Razón Social"
            hasFeedback
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
      )}
      <Col md={8} sm={24} xs={24}>
        <Form.Item
          label="Género"
          name="genero"
          normalize={capitalize}
        >
          <Select dataSource={generos} disabled={disabled} />
        </Form.Item>
      </Col>
      <Col md={8} sm={24} xs={24}>
        <Form.Item
          label="Estado civil"
          name="estado_civil"
          normalize={capitalize}
        >
          <Select dataSource={estadosCiviles} disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="correo_electronico"
          label="Correo electrónico"
          rules={rules.email}
          hasFeedback
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col md={16} sm={24} xs={24}>
        <LadaNumero
          form={form}
          rules={rules}
          fullSize
          names={{
            lada: 'lada',
            numero_de_telefono: 'numero_de_celular',
          }}
          celular
          disabled={disabled}
        />
      </Col>
      <>
        <Col span={24}>
          <Divider style={{ marginTop: 0 }}>Por Dirección</Divider>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <NumericInput
            label="Código postal"
            name="direccion__codigo_postal"
          />
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Calle principal"
            name="direccion__calle_principal"
            normalize={capitalize}
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Número exterior"
            name="direccion__numero_exterior"
            normalize={capitalize}
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      </>
    </Row>
  );

  const ContribuyenteForm = () => (
    <Row gutter={10}>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="clave"
          label="Clave"
          hasFeedback
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="nombre"
          label="Nombre"
          hasFeedback
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="apellido_paterno"
          label="Apellido paterno"
          hasFeedback
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="apellido_materno"
          label="Apellido materno"
          hasFeedback
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="razon_social"
          label="Razón Social"
          hasFeedback
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="correo_electronico"
          label="Correo electrónico"
          rules={rules.email}
          hasFeedback
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col md={16} sm={24} xs={12}>
        <LadaNumero
          form={form}
          rules={rules}
          fullSize
          names={{
            lada: 'lada',
            numero_de_telefono: 'telefono_celular',
          }}
          celular
          disabled={disabled}
        />
      </Col>
      <Col xs={24} sm={24} md={12}>
        <Form.Item
          name="rfc"
          label="RFC"
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={12}>
        <Form.Item
          label="CURP"
          name="curp"
          normalize={capitalize}
        >
          <Input allowClear />
        </Form.Item>
      </Col>
    </Row>
  );

  const DefaultPredioForm = () => (
    <Row gutter={10}>
      <Col md={6} sm={24} xs={24}>
        <Form.Item
          label="Clave"
          name="clave"
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} onBlur={() => handleOnBlur(form, 'clave')} />
        </Form.Item>
      </Col>
      {/* <Col md={6} sm={24} xs={24}>
        <Form.Item
          label="Cuenta única de predial"
          name="cuenta_unica_de_predial"
          normalize={capitalize}
        >
          <Input
            allowClear
            disabled={disabled}
            onBlur={() => handleOnBlur(form, 'cuenta_unica_de_predial')}
          />
        </Form.Item>
      </Col>
      <Col md={6} sm={24} xs={24}>
        <Form.Item
          label="CURT"
          name="CURT"
          normalize={(val) => val.toUpperCase()}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col md={6} sm={24} xs={24}>
        <Form.Item
          label="Clave catastral estándar"
          name="clave_catastral_estandar"
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col> */}
      <Col md={6} sm={24} xs={24}>
        <Form.Item
          label="No. Cuenta"
          name="clave_catastral_municipal"
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} onBlur={() => handleOnBlur(form, 'clave_catastral_municipal')} />
        </Form.Item>
      </Col>
      {ENV_NAME === 'nayarit' && (
      <Col md={6} sm={24} xs={24}>
        <Form.Item
          label="Propietario"
          name="nombres_de_participes"
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      )}
      {ENV_NAME === 'juarez' && (
      <InputSearchPro
        disabled={disabled}
        label="Ciudadano"
        name="q"
        copyableResult={false}
        breakPoints={{
          xs: 24,
          sm: 24,
          md: 12,
          xl: 12,
        }}
        inputProps={{
          onPressEnter: () => searchCiudadano(),
          onChange: ({ target: { value } }) => {
            if (!value) {
              resetAndValidate();
            }
          },
        }}
        resultProps={{
          breakPoints: {
            xs: 24,
            sm: 24,
            md: 12,
            xl: 12,
          },
          keyLabelRender: true,
          name: 'ciudadano',
          keyProp: 'clave_ciudadana',
          label: 'Ciudadano',
          dataSource: ciudadanoFound,
          labelProp: 'nombre_completo',
          onClear: resetAndValidate,
        }}
      />
      )}
      {!disabled && (
      <>
        <Col span={24}>
          <Divider style={{ marginTop: 0 }}>Por Dirección</Divider>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <NumericInput
            label="Código postal"
            name="direccion__codigo_postal"
          />
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Calle principal"
            name="direccion__calle_principal"
            normalize={capitalize}
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Número exterior"
            name="direccion__numero_exterior"
            normalize={capitalize}
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      </>
      )}
      <>
        <Col span={24}>
          <Divider style={{ marginTop: 0 }}>Por Propietario</Divider>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="RFC"
            name="rfc"
            normalize={capitalize}
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="CURP"
            name="curp"
            normalize={capitalize}
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Razón Social"
            name="razon_social"
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Nombres de Participes"
            name="nombres_de_participes"
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      </>
    </Row>
  );

  const forms = {
    // ciudadano
    1: (<CiudadanoForm />),
    // Empresa
    2: (
      <Row gutter={10}>
        <Col md={8} sm={24} xs={24}>
          <Form.Item
            label={TLAJOMULCO ? 'Objeto cuenta' : 'Clave'}
            name="clave"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col md={8} sm={24} xs={24}>
          <Form.Item
            label={TLAJOMULCO ? 'Denominación/Nombre comercial' : 'Nombre comercial'}
            name="nombre_comercial"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col md={8} sm={24} xs={24}>
          <Form.Item
            label="Página web"
            name="pagina_web"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col md={8} sm={24} xs={24}>
          <Form.Item
            label="RFC"
            rules={rules.RFC}
            name="RFC"
            normalize={(val) => val.toUpperCase()}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col md={8} sm={24} xs={24}>
          <Form.Item
            label="Tipo de establecimiento"
            name="tipo_de_establecimiento"
            normalize={capitalize}
          >
            <Select
              dataSource={tiposDeEstablecimiento}
              labelProp="nombre"
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="contribuyente__nombre_completo"
            label="Contribuyente (usar nombre completo)"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="ciudadano__nombre_completo"
            label="Ciudadano (usar nombre completo)"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        {!disabled && (
          <>
            <Col span={24}>
              <Divider style={{ marginTop: 0 }}>Por Dirección</Divider>
            </Col>
            <Col md={6} sm={24} xs={24}>
              <NumericInput
                label="Código postal"
                name="domicilio_fiscal__codigo_postal"
                normalize={capitalize}
              />
            </Col>
            <Col md={6} sm={24} xs={24}>
              <Form.Item
                label="Calle principal"
                name="domicilio_fiscal__calle_principal"
                normalize={capitalize}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col md={6} sm={24} xs={24}>
              <Form.Item
                label="Número exterior"
                name="domicilio_fiscal__numero_exterior"
                normalize={capitalize}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
    ),
    // Predio
    3: IPROVINAY ? (
      <Row gutter={10}>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Clave"
            name="clave"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Lote"
            name="lote_iprovinay"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Manzana"
            name="manzana_iprovinay"
            normalize={(val) => val.toUpperCase()}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        {!disabled && (
          <>
            <Col span={24}>
              <Divider style={{ marginTop: 0 }}>Por Dirección</Divider>
            </Col>
            <Col md={6} sm={24} xs={24}>
              <NumericInput
                label="Código postal"
                name="direccion__codigo_postal__d_codigo"
                maxLength={5}
              />
            </Col>
            <Col md={6} sm={24} xs={24}>
              <Form.Item
                label="Colonia"
                name="direccion__codigo_postal__d_asenta"
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col md={6} sm={24} xs={24}>
              <Form.Item
                label="Calle principal"
                name="direccion__calle_principal"
                normalize={capitalize}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col md={6} sm={24} xs={24}>
              <Form.Item
                label="Número exterior"
                name="direccion__numero_exterior"
                normalize={capitalize}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
          </>
        )}
        <>
          <Col span={24}>
            <Divider style={{ marginTop: 0 }}>
              <Row
                style={{
                  flexDirection: 'row',
                  width: 215,
                  alignItems: 'center',
                  rowGap: 10,
                }}
              >
                <Col span={24}>
                  <Form.Item
                    label="Tipo de Propietario"
                    name="tipo_propietario"
                    normalize={capitalize}
                  >
                    <Select
                      dataSource={padrones
                        .filter((p) => [padronesEnum.CIUDADANO, padronesEnum.CONTRIBUYENTE]
                          .includes(p.id))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Divider>
          </Col>
          <Col md={6} sm={24} xs={24}>
            <Form.Item
              label="Clave del propietario"
              name={tipoPropietario === padronesEnum.CIUDADANO
                ? 'ciudadano__clave_ciudadana' : 'contribuyente__clave'}
              normalize={capitalize}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col md={6} sm={24} xs={24}>
            <Form.Item
              label="Nombre del propietario"
              name="ciudadano__nombre_completo"
              normalize={capitalize}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col md={6} sm={24} xs={24}>
            <Form.Item
              label="CURP del propietario"
              name={tipoPropietario === padronesEnum.CIUDADANO
                ? 'ciudadano__CURP' : 'contribuyente__curp'}
              normalize={capitalize}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col md={6} sm={24} xs={24}>
            <Form.Item
              label="RFC del propietario"
              name={tipoPropietario === padronesEnum.CIUDADANO
                ? 'ciudadano__RFC' : 'contribuyente__rfc'}
              normalize={capitalize}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
        </>
      </Row>
    ) : (
      <DefaultPredioForm />
    ),
    // Vehiculos
    4: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Clave"
            name="clave"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Número de Placa"
            name="placa_vieja"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Serie"
            tooltip="Número de Identificación Vehícular"
            name="numero_de_identificacion_vehicular"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Clave de Repuve"
            name="clave_de_repuve"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Tarjeta de Circulación"
            name="tarjeta_de_circulacion"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
    ),
    // Contribuyente
    15: (<ContribuyenteForm />),
    // Motocicleta
    16: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Clave"
            name="clave"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Número de Placa"
            name="placa_vieja"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            name="serie"
            label="Serie"
            tooltip="Número de Serie de la Motocicleta"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Registro Publico"
            name="registro_publico_vehicular"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Tarjeta de Circulación"
            name="tarjeta_de_circulacion"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
    ),
    17: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Clave"
            name="clave"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Número de Placa"
            name="placa_vieja"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            name="serie"
            label="Serie"
            tooltip="Número de Serie del Remolque"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Registro Público"
            name="registro_publico_vehicular"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Tarjeta de Circulación"
            name="tarjeta_de_circulacion"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
    ),
    18: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="clave_de_anexo"
            label="Clave de anexo"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="clave_de_cuenta"
            label="Clave de Cuenta"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="folio"
            label="Folio"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="serie"
            label="Serie"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="tipo_de_contrato"
            label="Tipo de Contrato"
            hasFeedback
            allowClear
          >
            <Select dataSource={tipoDeContrato} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="empresa__nombre_comercial"
            label="Empresa"
            hasFeedback
            allowClear
            tooltip="Buscar por nombre comercial"
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      </Row>
    ),
    19: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="clave"
            label="clave"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="folio"
            label="Folio"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="serie"
            label="Serie"
            hasFeedback
          >
            <Input maxLength={20} allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="numero_de_placa"
            label="Número de Placa"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="numero_de_licencia"
            label="Número de Licencia"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="nombre_completo"
            label="Nombre de Propietario"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      </Row>
    ),
    20: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="clave"
            label="Clave"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="contribuyente__nombre_completo"
            label="Contribuyente (usar nombre completo)"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      </Row>
    ),
    21: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="clave"
            label="Clave"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="contribuyente__nombre_completo"
            label="Contribuyente (usar nombre completo)"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="ciudadano__nombre_completo"
            label="Ciudadano (usar nombre completo)"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      </Row>
    ),
    22: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="clave_de_control"
            label="Clave de Control"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="empresa__nombre_comercial"
            label="Empresa"
            hasFeedback
            allowClear
            tooltip="Buscar por nombre comercial"
          >
            <Input allowClear />
          </Form.Item>
        </Col>

      </Row>
    ),
    23: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="clave"
            label="clave"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="tipo_de_zona_exclusiva"
            label="Tipo de zona exclusiva"
            hasFeedback
          >
            <Select dataSource={tiposDeZonaExclusiva} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="empresa__nombre_comercial"
            label="Empresa"
            hasFeedback
            allowClear
            tooltip="Buscar por nombre comercial"
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <>
          <Col span={24}>
            <Divider style={{ marginTop: 0 }}>Por Dirección</Divider>
          </Col>
          <Col md={6} sm={24} xs={24}>
            <NumericInput
              label="Código postal"
              name="direccion__codigo_postal"
            />
          </Col>
          <Col md={6} sm={24} xs={24}>
            <Form.Item
              label="Calle principal"
              name="direccion__calle_principal"
              normalize={capitalize}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col md={6} sm={24} xs={24}>
            <Form.Item
              label="Número exterior"
              name="direccion__numero_exterior"
              normalize={capitalize}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
        </>
      </Row>
    ),
    24: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="clave"
            label="clave"
            hasFeedback
            required
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="numero_de_poliza"
            label="Número de Poliza"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="empresa__nombre_comercial"
            label="Empresa"
            hasFeedback
            allowClear
            tooltip="Buscar por nombre comercial"
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <>
          <Col span={24}>
            <Divider style={{ marginTop: 0 }}>Por Dirección</Divider>
          </Col>
          <Col md={6} sm={24} xs={24}>
            <NumericInput
              label="Código postal"
              name="direccion__codigo_postal"
            />
          </Col>
          <Col md={6} sm={24} xs={24}>
            <Form.Item
              label="Calle principal"
              name="direccion__calle_principal"
              normalize={capitalize}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col md={6} sm={24} xs={24}>
            <Form.Item
              label="Número exterior"
              name="direccion__numero_exterior"
              normalize={capitalize}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
        </>
      </Row>
    ),
    25: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="clave"
            label="Clave"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="folio"
            label="Folio"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="folio_interno"
            label="Folio Interno"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="identificacion"
            label="Identificación"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      </Row>
    ),
    31: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="folio"
            label="Folio"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="panteon"
            label="Panteón"
            hasFeedback
          >
            <Select dataSource={tiposDePanteones} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="tipo_contrato"
            label="Contrato"
            hasFeedback
          >
            <Select dataSource={tipoDeContratoPanteones} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="espacio"
            label="Espacio"
            hasFeedback
          >
            <Select dataSource={tiposDeEspacios} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="contribuyente__nombre_completo"
            label="Contribuyente (usar nombre completo)"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="ciudadano__nombre_completo"
            label="Ciudadano (usar nombre completo)"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      </Row>
    ),
    32: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="clave"
            label="Objeto Cuenta"
            hasFeedback
            allowClear
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="contribuyente__nombre_completo"
            label="Contribuyente (usar nombre completo)"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="zona_de_modulo"
            label="Zona de Modulos"
            hasFeedback
          >
            <Select dataSource={zonas} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="numero_de_puesto"
            label="Local"
            hasFeedback
          >
            <Input allowClear />
          </Form.Item>
        </Col>
      </Row>
    ),
  };

  return forms?.[tipoDePadron] || <DefaultForm />;
};

BusquedaFormsPadrones.propTypes = {
  tipoDePadron: PropTypes.number.isRequired,
  visibleRFC: PropTypes.bool,
  visibleCURP: PropTypes.bool,
  form: PropTypes.shape().isRequired,
  catalogs: PropTypes.shape({
    generos: PropTypes.arrayOf(PropTypes.shape()),
    estadosCiviles: PropTypes.arrayOf(PropTypes.shape()),
    tiposDeEstablecimiento: PropTypes.arrayOf(PropTypes.shape()),
    unidadesRecaudadoras: PropTypes.arrayOf(PropTypes.shape()),
    tiposDeZonaExclusiva: PropTypes.arrayOf(PropTypes.shape()),
    tipoDeContrato: PropTypes.arrayOf(PropTypes.shape()),
    tiposDeVehiculos: PropTypes.arrayOf(PropTypes.shape()),
    lineasVehiculares: PropTypes.arrayOf(PropTypes.shape()),
    marcasDeVehiculos: PropTypes.arrayOf(PropTypes.shape()),
    clasesDeVehiculos: PropTypes.arrayOf(PropTypes.shape()),
    serviciosDelVehiculo: PropTypes.arrayOf(PropTypes.shape()),
    estadosDelVehiculo: PropTypes.arrayOf(PropTypes.shape()),
    tipoDeContratoPanteones: PropTypes.arrayOf(PropTypes.shape()),
    tiposDeEspacios: PropTypes.arrayOf(PropTypes.shape()),
    tiposDePanteones: PropTypes.arrayOf(PropTypes.shape()),
    perimetros: PropTypes.arrayOf(PropTypes.shape()),
    tipoDePuesto: PropTypes.arrayOf(PropTypes.shape()),
    zonas: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  setLoading: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  ciudadanoFound: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  setCiudadanoFound: PropTypes.func.isRequired,
};

BusquedaFormsPadrones.defaultProps = {
  disabled: false,
  visibleRFC: true,
  visibleCURP: true,
};

export default BusquedaFormsPadrones;
